import { fluidType } from 'Shared/common/FluidType';
import { sanitise } from 'Shared/common/Sanitise';
import { merge } from 'Shared/common/merge';
import { standardPoster } from 'Shared/frontend/themes/standardPoster';
import { standardText } from 'Shared/frontend/themes/StandardText';
import {ILinks} from 'Common/config/ILinks';
import { VenueRoutes } from 'Shared/routes/VenueRoutes';
import { standardLinks } from 'Shared/frontend/themes/standardLinks';

import {Calendar as BackendCalendar} from 'Shared/backend/icons/Calendar';
import {FoodMenu as BackendMenu} from 'Shared/backend/icons/FoodMenu';
import {InfoCircle as BackendInfoCircle} from 'Shared/backend/icons/InfoCircle';
import {StarCircle as BackendStarCircle} from 'Shared/backend/icons/StarCircle';

import { Calendar as FrontendCalendar } from 'Shared/frontend/icons/Calendar';
import { Contact as FrontendContact } from 'Shared/frontend/icons/Contact';
import { FoodMenu as FrontendMenu } from 'Shared/frontend/icons/FoodMenu';
import { Home as FrontendHome } from 'Shared/frontend/icons/Home';
import { standardStyles } from 'Shared/frontend/styles/standardStyles';
import { NavSchema } from 'Shared/frontend/nav/navSchema';

export abstract class BaseSettings
{
	sizes()
	{
		return {
			flipToMobileNav: 600,

			/* Sometimes its good to shrink the margins a bit when approaching flipToMobileNav... */
			startMarginShrink: 700,
			widePageSideMargin: 32,
			narrowPageSideMargin: 16,
			minDesktopWidth: 1000,
			maxPageWidth: 1200, 
			maxFooterWidth: 1300,

			/* Width of an iPhone5 is 320px, iPhone6 is 375px */
//XXX cf ==> minWidth / minPhoneWidth?			
			endFontShrink: 375,

			/* Events use a different flip point because they are so busy */
//TODO look at this use. Probably better moved to the individual page files.
			flipToOneColumn: 700,	
		};
	}

	abstract palette();

	abstract fontIncludes();

	abstract fonts();

	poster()
	{
		return standardPoster(this.palette(),this.fonts());
	}

	text()
	{
		return standardText(this.sizes(),this.fonts(),this.palette());
	}

	address()
	{
		return {
			...sanitise.address,
			lineHeight: 'normal',
			display: 'inline-block',
			verticalAlign: 'middle',
		};
	}

	footer()
	{
		return {
			address: this.address(),

			divider: {
			//	borderColor: 'inherit'
				borderColor: this.palette().feature
			},
			title: {
				color: this.palette().feature,
				fontFamily: this.fonts().heading,
				fontWeight: 700,
				textTransform: 'uppercase',
				margin: '8px 0',
			},
		};
	}

	nav() {
		const sizes = this.sizes();
		const mobileBackgroundColour = this.palette().background;

		return {
			desktop: <NavSchema> {
				/* FOR OPTIONS: see the NavSchema definition */

//TODO implement 'hamburger' + 'none'
				type: 'standard', 

				settings: {
					logoPosition: 'left',

					sticky: true,
//NOTE fadeBackgroundIn does not currently support subnavs. Probably just want to use it for homepages.
//					fadeBackgroundIn: true, 

					outer: {
//						background: this.palette().feature,
						background: this.palette().background,
					},

					inner: {
						height: 100,
						maxWidth: 950,
						/* Scavenge some space on narrow screens: */
						gap: fluidType('em',sizes.flipToMobileNav/16,sizes.minDesktopWidth/16,0,2)
					},

					logo: {
						height: '100%',
						padding: '5px 0 5px 0'
					},

					button: {
						...sanitise.button,
						fontFamily: this.fonts().desktopNav,
						fontSize: fluidType('px',sizes.endFontShrink,sizes.minDesktopWidth,16,30),
						backgroundColor: 'inherit',
						color: this.palette().text,
						margin: '0 0.4em',
						textTransform: 'uppercase',
						padding: '0 0',
					},
					active: {
						color: this.palette().feature
					},
					hover: {
						color: this.palette().feature
					},
					subNav: {
						background: this.palette().background,
					}
				}
			},
			mobile: {
				overlay:{},

				button: {
					...sanitise.button,
					fontFamily: this.fonts().mobileNav,
					backgroundColor: mobileBackgroundColour,
					color: this.palette().text,
					contents: {
						svg:{
							fill: this.palette().text,
						},
					},
					padding: '0.6em 0 18px 0',

					/* hover is bit academic on mobile: */
					':hover': {
						//XXX not sure how well this will work with a light theme
						backgroundColor: `hsl(from ${mobileBackgroundColour} h calc(s + 15) calc(l + 15))`
					}
				},

				active: {
					color: this.palette().feature
				},

				more: {
					root: {},
					button: {
						textTransform: 'capitalize',  //XXX undoing theme.button
						borderStyle: 'none',
						color: '#e2e2e2',
						backgroundColor: 'transparent',
						padding: '0.8em 0',
						fontSize: fluidType('px',this.sizes().endFontShrink,this.sizes().flipToMobileNav,20,30),
						':hover': {
							backgroundColor: this.palette().dimBackground
						}
					},
					address: this.address(),
					phone: {}
				}
			}
		};
	}

//TODO create a button function too?

	subNavButton()
	{
		return {
			...sanitise.button,
			fontFamily: this.fonts().subNav,
			fontSize: 15,
			textTransform: 'capitalize',
			borderWidth: 1,
			//FIXME inherit new 
			backgroundColor: this.palette().dimBackground,
			color: this.palette().text,
			margin: '0 0.4em',
			padding: '0.44em 2.25em',
		};
	}

	page()
	{
		const palette = this.palette();
		const fonts = this.fonts();
		const sizes = this.sizes();
		const text = this.text();

		return {
			text: text,

			/* The root settings are inherited through the page */
			root: {
				backgroundColor: palette.background,
				color: palette.text,

				//TODO delete these font lines soon I think...
				fontFamily: fonts.text,
				lineHeight: 1.5,
				/*
					fontSize=15 looks a bit neater than 16, but note that this results in all 'ems' being multiplied by 15/16,
					which is horrible. Suggests this rule:
					- don't use 'em' as a unit except for those margins and padding that are tied to the size of the local text.
					  It should be avoided in cases where these things are being used for larger-scale layouts.
					  It shouldn't be used for defining other font sizes (eg titles).
				*/
				fontSize: 15
			},

			nav: this.nav(),

			footer: this.footer(),

			header: {
				desktop: {
					display: 'none',
				},
				mobile: {
					display: 'none',
					padding: '0.5em 0 1em 0'
				},
				logo: {},
			},

			poster: this.poster(),

			sideMargins: {
				left: fluidType('px',sizes.flipToMobileNav,sizes.startMarginShrink,sizes.narrowPageSideMargin,sizes.widePageSideMargin),
				right: fluidType('px',sizes.flipToMobileNav,sizes.startMarginShrink,sizes.narrowPageSideMargin,sizes.widePageSideMargin),
			},

			main: {
				flexGrow: 1,
				marginTop: 0,
				marginBottom: 32,
				maxWidth: sizes.maxPageWidth,
			},

			section: {
				width: '100%',
				textAlign: 'center',
			},

			subNav: {
				inner: {
					marginTop: 32,
					marginBottom: 32
				}
			},

			widgets: {    //XXX cf ==> layout/design/pageDesign/showcase or promote slideshow 
				scrollStyle:{
					scrollbarColor: 'white #666363',
					
				},
				
				overlay: {},
				slideshow: {    //TODO rename to slides for consistency
					teaser: { },
					backgroundColour: palette.dimBackground,

					overlayedImage: {
						backgroundColor: 'rgba(0, 0, 0, 0.56)',
						padding: '2em',
						minWidth: '395px',

						filter: {},
					}
				},

				subNav: {
					button: this.subNavButton()
				},
				visualWithInfo: {
					wide: {
						float: {
	    					width: '50%',
							paddingTop: 16,
							//FIXME currently this means the side alignment is off. Adding a bit of padding does reduce the size of the image a bit, but there must bit a better way
							outerPadding: 8,
							innerPadding: 16,
							innerMargin: '1%'
						},
						textAlign: 'start'
					},
					narrow: {
						textAlign: 'center'
					}
				},
				grid: {
					columnGap: '3vw',
					rowGap: '4vw',
				}
			},

			a: {
				...sanitise.a,
				color: palette.feature
			},

			list: {
				...sanitise.list,
				lineHeight: 1.4,
			},

			definitions: {
				...sanitise.definitions,
				lineHeight: 1,

				dt: {
					...sanitise.definitions.dt,
					fontWeight: 'bold',
					paddingRight: '0.6em'
				},

				dl: {
					...sanitise.definitions.dl,
				}
			},

			overlay: {
				button: {
					...sanitise.button,
					fontFamily: fonts.subNav,
					fontSize: 44,
					fontWeight: 700,
					borderRadius: '50%',
					width: 44,
					height: 44,
					padding: 0,
					color: palette.text,
					backgroundColor: palette.background,
				}
			},

			blockquote:  {
				...sanitise.blockquote,
				fontStyle: 'italic',
				fontWeight: 300,
				lineHeight: 1.3,
				margin: '1em 0'
			},

			address: this.address(),

			iframe: {
				...sanitise.iframe
			},

			button: {
				...sanitise.button,
				fontSize: 15,
				fontFamily: fonts.buttons,
				color: palette.text,
				borderWidth: 1,
				padding: '0.7em 2.25em',
				textTransform: 'uppercase'
			},

			embeddedHtml: {
//XXX something in staffResources might be using h1 directly				
				h1: { 
					...text.paragraphTitle,
				},
				'h2,h3,h4,h5,h6': {
					...text.paragraphTitle,
				},

				strong: {
					fontWeight: 700,
				},

				a: {
					...sanitise.a,
					color: palette.feature
				},

				p: {
					...text.plain,
					margin: '16px 0',
					lineHeight: 1.5
				},

				img: {
					maxWidth: '100%',
					height: 'auto'
				}
			},
		};
	}

	pages() { return []; }


	siteTheme()
	{
		const palette = this.palette();
		const fonts = this.fonts();
		const sizes = this.sizes();
		const page = this.page();

		return {
			sizes: sizes, 	//TODO maybe rename to 'mediaPoints', 'turningPoints', 'pageWidths', 'widths' or something

			palette: palette,

			fonts: fonts,

			page: page,

			fontIncludes: this.fontIncludes(),

			specials: {
				general: 'General'
			},

			classes: [  
				'Page heading',
				// Examples:   'Featured', 'Special page'
				'testClass',
			],

			styles: standardStyles(sizes,this.text()),

			event: merge(this.page(), {
				subNav: {
					button: this.subNavButton()
				},
				heading: {
					...page.text.title,

//TODO? Here I undo the default page heading setting by including textAlign and colour. Maybe add a mechanism to replace or delete a property- 
//      eg page.delete('heading').merge().  Good if it could take a path and not just delete topmost items.					

					textAlign: 'left',
					color: palette.text 
				},
				artist: {
					heading: {
						...page.text.title,
					
						fontSize: fluidType('px',sizes.endFontShrink,sizes.minDesktopWidth,27,46),
						

					},
					wide: {
						nameAndTime: {}
					},
					narrow: {
						nameAndTime: {
							backgroundColor: palette.text
						}
					}, 
					genresAndLocation: {} // for overriding
				}
			}),

//XXX could potentially pass 'props' to menu instead, or to the whole theme
			menu: merge(page, {
				subNav: {
					button: this.subNavButton(),
//XXX separating out the customisation part so 'button' can be patched by TheTownieTheme etc				
//    Consider patching the result of a function call when it exists. cf efficiency.				
					buttonType: type => {{}}
				},
				section: {
					marginTop: 16,
					marginBottom: 48,
				},

				title:(level:number,productType,section) => {
					return {
						...page.text.title,
						textAlign: 'center',
						...(level > 2 ? {fontSize:'clamp(16px, 10vw, 35px)'} : {}),
					}
				},
				item: {
					type: 'grid',
					title: {},
					description: {},
					//XXX The Townie uses different colours for different section types, so using closure here
					titleType: section => {},
					//XXX actually applied to 'priceWrap'...
					price: {},
				}
			}),

			staffResources: merge(page, {
				subNav: {
					inner: {
						marginTop: 16,
						marginBottom:16 ,
					}
				}
			}),

			staffResource: merge(page, {
				sideMargins: {
					left: 0,
					right:0 
				}
			}),
		};
	}

	navContent()
	{
		return {
			desktop: [
				{label:'Home',		link:'frontend/Home'},
				{label:'Menu',		link:'frontend/Menu'},
				{label:'Events',	link:'frontend/Events'},
				{label:'Hire',		link:'frontend/Hire'},
				{label:'Contact',	link:'frontend/Contact'}
			],
			mobile: {
				maxButtons: 5,
				includeMoreButton: true,
				links: [
					{label:'Home',		link:'frontend/Home',		icon: FrontendHome},
					{label:'Menu',		link:'frontend/Menu',		icon: FrontendMenu},
					{label:'Events',	link:'frontend/Events',		icon: FrontendCalendar},
					{label:'Hire',		link:'frontend/Hire', 		icon: FrontendContact},
					{label:'Contact',	link:'frontend/Contact',	icon: FrontendContact},
				]
			}
		};
	}

	backendNavContent()
	{
		return [
			{label:'Events',	route:'/admin/events',			icon:BackendCalendar},
			{label:'Menu',		route:'/admin/menus',			icon:BackendMenu},
			{label:'Specials',	route:'/admin/specials',		icon:BackendStarCircle},
			{label:'Info',		route:'/admin/info',			icon:BackendInfoCircle},
		];
	}

	/* IDEA these backend event settings could be accessed via a gear settings icon on the event entry page */
	backendEvent()
	{
		return {
			calendar: {
				singleEvent: {
					defaultStart: {
						hour: 12,
						minute: 0
					}
				}
			}
		};
	}

	links(): ILinks
	{
		return standardLinks();
	}

	routes()
	{
		return VenueRoutes;
	}
}

export type BaseThemeType = ReturnType<BaseSettings['siteTheme']>;

export type BasePageThemeType = ReturnType<BaseSettings['page']>;

export type BaseSizesType = ReturnType<BaseSettings['sizes']>;

